import { useState, useCallback, useRef } from 'react';

interface UseTipsCarouselOptions {
  totalItems: number;
  onClose?: () => void;
}

interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
}

interface UseTipsCarouselReturn {
  currentIndex: number;
  canGoBack: boolean;
  canGoForward: boolean;
  isLastTip: boolean;
  goNext: () => void;
  goPrev: () => void;
  goTo: (index: number) => void;
  swipeHandlers: SwipeHandlers;
}

const SWIPE_THRESHOLD = 50;

export const useTipsCarousel = ({ totalItems, onClose }: UseTipsCarouselOptions): UseTipsCarouselReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchCurrentX = useRef<number | null>(null);

  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < totalItems - 1;
  const isLastTip = currentIndex === totalItems - 1;

  const goNext = useCallback(() => {
    if (canGoForward) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [canGoForward]);

  const goPrev = useCallback(() => {
    if (canGoBack) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [canGoBack]);

  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalItems) {
        setCurrentIndex(index);
      }
    },
    [totalItems]
  );

  // Swipe gesture handlers
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchCurrentX.current === null) {
      return;
    }

    const diff = touchStartX.current - touchCurrentX.current;

    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      if (diff > 0) {
        // Swiped left -> go next
        goNext();
      } else {
        // Swiped right -> go prev
        goPrev();
      }
    }

    touchStartX.current = null;
    touchCurrentX.current = null;
  }, [goNext, goPrev]);

  const swipeHandlers: SwipeHandlers = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };

  return {
    currentIndex,
    canGoBack,
    canGoForward,
    isLastTip,
    goNext,
    goPrev,
    goTo,
    swipeHandlers,
  };
};
